import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AssetTypeServices from '@Services/AssetType.services.ts';
import { AssetType } from '@Types/Asset.types';
import { AssetTypeFormFields } from '@Types/Form.types';
import { formatRequired } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { ErrorDisplay, FreeText, Toast } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  assetType?: AssetType;
};

const { ASSET_TYPE_RULES } = VALIDATION_RULES;
const AssetTypeModal = ({ show, onClose, onSuccess, assetType }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue,
  } = useForm<AssetTypeFormFields>();

  useEffect(() => {
    reset();
    if (assetType) {
      setValue('assetCode', assetType?.assetCode);
      setValue('description', assetType.description);
    }
  }, [assetType, show]);

  const onSubmitSuccess = () => {
    Toast.success(assetType ? 'Asset type updated successfully.' : 'Asset type created successfully.');
    onSuccess();
    reset();
    onClose();
  };

  const handleCreateAssetType = (data: AssetTypeFormFields) => {
    AssetTypeServices.createAssetType(data)
      .then(() => onSubmitSuccess())
      .catch((error) => handleRequestError(error, setErrorMessage, true));
  };
  const handleUpdateAssetType = (data: AssetTypeFormFields) => {
    AssetTypeServices.updateAssetType(assetType?.id, data)
      .then(() => onSubmitSuccess())
      .catch((error) => handleRequestError(error, setErrorMessage, true));
  };

  const title = assetType ? 'Edit Asset Type' : 'Add Asset Type';
  const handleLocationSumbit = assetType ? handleUpdateAssetType : handleCreateAssetType;

  return (
    <div className='modal--fit-content modal--pt-25vh modal--w-40vw'>
      <ModalWrapper
        title={title}
        primaryButtonText='Confirm'
        show={show}
        handleClose={onClose}
        primaryAction={() => handleSubmit(handleLocationSumbit)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form onSubmit={() => handleSubmit(handleLocationSumbit)()}>
            <div className='grid'>
              <div className='col-12'>
                <FreeText
                  label={formatRequired('Code')}
                  fullWidth
                  error={!!errors.assetCode}
                  errorMessage={errors.assetCode?.message}
                  {...register('assetCode', ASSET_TYPE_RULES.CODE)}
                />
                <FreeText
                  label={formatRequired('Description')}
                  fullWidth
                  error={!!errors.description}
                  errorMessage={errors.description?.message}
                  {...register('description', ASSET_TYPE_RULES.DESCRIPTION)}
                />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default AssetTypeModal;
